import { useState, type FormEvent, type ReactNode } from "react";
import { RULE_EFFECTS, type RuleDTO, type RuleEffect } from "@roomate/core";
import { keys, useCreate, useDelete, useMe, usePatch, useRules } from "../api.ts";
import { cx, ErrorNote, inputCls } from "../ui.tsx";
import { Action, Bento, Disclosure, Page, PageHeader, Pill, Ring, Tile } from "../calm.tsx";

const EFFECT_TEXT: Record<RuleEffect, string> = {
  forbid_agent_builder: "agents may not build it",
  require_human_reviewer: "a human must review it",
  require_focus: "a reviewer with this focus is required:",
  min_tier: "risk is at least",
  require_reviewer: "this person must review:",
};

const EFFECT_LABEL: Record<RuleEffect, string> = {
  forbid_agent_builder: "Agents blocked",
  require_human_reviewer: "Human review",
  require_focus: "Needs focus",
  min_tier: "Minimum tier",
  require_reviewer: "Named reviewer",
};

const EFFECT_TONE: Record<RuleEffect, "danger" | "signal" | "neutral"> = {
  forbid_agent_builder: "danger",
  require_human_reviewer: "signal",
  require_focus: "signal",
  min_tier: "neutral",
  require_reviewer: "signal",
};

export function RulesPage() {
  const rules = useRules().data ?? [];
  const me = useMe().data!;
  const admin = me.access === "admin";
  const patch = usePatch([keys.rules]);
  const del = useDelete([keys.rules]);
  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <Page>
      <PageHeader title="Rules" meta={<span className="text-ink-3">Routing cannot override them; assignments that break one are refused.</span>} />

      <Bento className="mt-8">
        <Tile span={6} className="flex flex-wrap items-center gap-6">
          <Ring value={enabledCount} total={rules.length} tone={rules.length > 0 && enabledCount === rules.length ? "ok" : "ink"} size={72} />
          <div className="min-w-0">
            <p className="text-[17px] font-medium leading-snug">{rules.length === 0 ? "No rules yet" : `${enabledCount} of ${rules.length} rule${rules.length === 1 ? "" : "s"} active`}</p>
            <p className="mt-1.5 text-[13.5px] text-ink-2">Hard rules run before routing scores anything; agents can't bend them.</p>
          </div>
        </Tile>

        {rules.map((r) => (
          <RuleTile
            key={r.id}
            rule={r}
            admin={admin}
            pending={(patch.isPending && patch.variables?.path === `/rules/${r.id}`) || (del.isPending && del.variables === `/rules/${r.id}`)}
            onToggle={() => patch.mutate({ path: `/rules/${r.id}`, body: { enabled: !r.enabled } })}
            onDelete={() => del.mutate(`/rules/${r.id}`)}
          />
        ))}

        {admin && <NewRuleTile />}
      </Bento>

      <ErrorNote error={patch.error ?? del.error} />
    </Page>
  );
}

/** One rule as a sentence: what it matches, what it does, editing tucked away. */
function RuleTile({ rule, admin, pending, onToggle, onDelete }: { rule: RuleDTO; admin: boolean; pending: boolean; onToggle: () => void; onDelete: () => void }) {
  return (
    <Tile span={3} className={cx(!rule.enabled && "opacity-60")} testId="rule-card">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[15px] font-medium" data-testid="rule-name">
          {rule.name}
        </p>
        <Pill tone={EFFECT_TONE[rule.effect]}>{EFFECT_LABEL[rule.effect]}</Pill>
        {!rule.enabled && <Pill tone="neutral">Off</Pill>}
      </div>
      <p className="mt-2 text-[14px] leading-relaxed text-ink-2">{ruleSentence(rule)}</p>
      {admin && (
        <Disclosure summary={<>Edit <span className="sr-only">{rule.name}</span></>} className="mt-3">
          <div className="flex flex-wrap gap-2">
            <Action onClick={onToggle} disabled={pending}>
              {rule.enabled ? "Disable" : "Enable"}
            </Action>
            <Action danger disabled={pending} onClick={() => confirm(`Delete “${rule.name}”?`) && onDelete()}>
              Delete
            </Action>
          </div>
        </Disclosure>
      )}
    </Tile>
  );
}

function codeChip(text: string) {
  return (
    <code key={text} className="break-all rounded bg-ink/[0.06] px-1 py-0.5 text-[12.5px]">
      {text}
    </code>
  );
}

function ruleSentence(r: RuleDTO): ReactNode {
  const areas = r.match.areas ?? [];
  const labels = r.match.labels ?? [];
  const kinds = r.match.kinds ?? [];
  const when: ReactNode[] = [];
  if (areas.length) {
    when.push(
      <span key="areas">
        touches{" "}
        {areas.map((a, i) => (
          <span key={a}>
            {i > 0 && (i === areas.length - 1 ? " or " : ", ")}
            {codeChip(a)}
          </span>
        ))}
      </span>,
    );
  }
  if (labels.length) when.push(<span key="labels">is labelled {labels.join(" or ")}</span>);
  if (kinds.length) when.push(<span key="kinds">is a {kinds.join(" or ")}</span>);

  return (
    <>
      When a task {when.length > 0 ? when.flatMap((n, i) => (i === 0 ? [n] : [" and ", n])) : "is routed"}: <strong className="font-medium text-ink">{EFFECT_TEXT[r.effect]}</strong>
      {r.value ? <> {codeChip(r.value)}</> : null}.
    </>
  );
}

function NewRuleTile() {
  const [name, setName] = useState("");
  const [areas, setAreas] = useState("");
  const [labels, setLabels] = useState("");
  const [effect, setEffect] = useState<RuleEffect>("require_human_reviewer");
  const [value, setValue] = useState("");
  const create = useCreate("/rules", [keys.rules]);
  const needsValue = effect === "require_focus" || effect === "min_tier" || effect === "require_reviewer";
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const list = (s: string) =>
      s
        .split(/[,\n]/)
        .map((x) => x.trim())
        .filter(Boolean);
    create.mutate(
      { name, match: { areas: list(areas), labels: list(labels) }, effect, value: needsValue ? value : null },
      { onSuccess: () => (setName(""), setAreas(""), setLabels(""), setValue("")) },
    );
  };
  return (
    <Tile span={6} testId="new-rule">
      <Disclosure summary="Add a rule">
        <form onSubmit={submit} className="mt-2 grid gap-3 sm:grid-cols-2">
          <TextField className="sm:col-span-2" label="Name" value={name} onChange={setName} placeholder="Agents don't touch the schema" />
          <TextField label="Areas" hint="globs, comma-separated" mono value={areas} onChange={setAreas} placeholder="**/schema/**" />
          <TextField label="Labels" hint="optional" value={labels} onChange={setLabels} />
          <label className="block">
            <span className="text-[13px] text-ink-2">Effect</span>
            <select className={cx(inputCls, "mt-1")} value={effect} onChange={(e) => setEffect(e.target.value as RuleEffect)}>
              {RULE_EFFECTS.map((r) => (
                <option key={r} value={r}>
                  {EFFECT_LABEL[r]}
                </option>
              ))}
            </select>
          </label>
          {needsValue && (
            <TextField
              label="Value"
              hint={effect === "min_tier" ? "low, medium, high or critical" : effect === "require_focus" ? "e.g. security" : "actor id"}
              value={value}
              onChange={setValue}
            />
          )}
          <div className="flex items-center gap-3 sm:col-span-2">
            <Action primary type="submit" disabled={!name.trim() || create.isPending}>
              Add rule
            </Action>
            <ErrorNote error={create.error} />
          </div>
        </form>
      </Disclosure>
    </Tile>
  );
}

function TextField({ label, hint, value, onChange, placeholder, mono, className }: { label: string; hint?: string; value: string; onChange: (v: string) => void; placeholder?: string; mono?: boolean; className?: string }) {
  return (
    <label className={cx("block", className)}>
      <span className="text-[13px] text-ink-2">
        {label}
        {hint && <span className="ml-1.5 text-ink-3">{hint}</span>}
      </span>
      <input className={cx(inputCls, "mt-1", mono && "font-mono")} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </label>
  );
}
